<template>
  <div class="agreement-page">
    <div class="background-blur"></div>
    <div class="content-wrapper">
      <div class="agreement-content">
        <h2>复课证明知情同意书</h2>
        <p class="no-indent">尊敬的学生家长：</p>
        <p class="p1">您好！为方便我中心辖区内学校学生家长开具复课证明，现已开通传染病线上复课证明办理服务。请您仔细阅读以下内容并确认同意：</p>
        <p class='p2'> 1.信息用途：您所填写的所有信息仅用于开具传染病复课证明，确保信息真实、准确。<br>
         2.病情要求：学生所患症状消失、疾病已痊愈，且提供的相关材料真实有效。<br>
         3.法律责任：如因隐瞒病情导致疫情传播或扩散，您将承担由此产生的全部法律责任。</p>
        <p class="p3">目前，仅以下传染病可通过线上申请复课证明，具体要求如下：</p>
        <ul>
          <li class="li1">流行性感冒：体温恢复正常、咳嗽及咽痛症状基本消失后48小时（体温恢复正常、咳嗽及咽痛症状基本消失后可申请）。</li>
          <li class="li1">水痘：隔离至疱疹全部结痂，且不少于病后14天（发病后至少12天可申请）。</li>
          <li class="li1">手足口病、疱疹性咽峡炎：隔离至发病后14天（发病后至少12天可申请）。</li>
          <li class="li1">流行性腮腺炎：隔离至发病后14天（至少12天可申请）。</li>
          <li class="li1">发病后其他感染性腹泻：隔离至症状消失后72小时，症状消失后至少2天可申请。</li>
        </ul>
      </div>

      <div class="signature-area">
        <p>家长/监护人手写签名：</p>
        <div class="signature-preview" @click="openSignatureModal" v-if="!signatureImage">
          <span class="sign-hint">点击此处签名</span>
        </div>
        <img v-else :src="signatureImage" alt="签名预览" class="signature-img">
        
        <van-button 
          class="sign-btn" 
          type="default" 
          @click="openSignatureModal" 
          :disabled="isDisabled || signatureImage"
        >
          {{ isDisabled ? `请仔细阅读 ${countdown} 秒` : signatureImage ? "签名已完成" : "请签名" }}
        </van-button>
      </div>

      <van-button 
        type="primary" 
        class="next-btn custom-btn" 
        @click="handleConfirm"
        :disabled="!signatureImage || isSubmitting"
      >
        {{ isSubmitting ? "提交中..." : "确认" }}
      </van-button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onActivated, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import request from '@/utils/request';
import { Button as VanButton } from 'vant';

// 响应式状态定义
const signatureImage = ref('');
const countdown = ref(10);
const isDisabled = ref(true);
const isSubmitting = ref(false);
const timer = ref(null);

// 路由实例
const router = useRouter();

// 组件挂载时执行
onMounted(() => {
  // 从 sessionStorage 获取签名图片
  const savedSignature = sessionStorage.getItem('signatureImage');
  if (savedSignature) {
    signatureImage.value = savedSignature;
  }

  // 检查是否已完成倒计时（避免重复计时）
  const hasRead = sessionStorage.getItem('hasReadAgreement');
  if (hasRead) {
    isDisabled.value = false;
    countdown.value = 0;
    return;
  }

  // 启动首次倒计时
  startCountdown();
});

// 组件激活时执行（从签名页返回时）
onActivated(() => {
  const savedSignature = sessionStorage.getItem('signatureImage');
  if (savedSignature) {
    signatureImage.value = savedSignature;
  }
});

// 组件销毁前执行
onBeforeUnmount(() => {
  if (timer.value) {
    clearInterval(timer.value);
  }
});

/**
 * 启动倒计时
 */
const startCountdown = () => {
  if (timer.value) {
    clearInterval(timer.value);
  }

  countdown.value = 10;
  isDisabled.value = true;

  timer.value = setInterval(() => {
    countdown.value--;

    if (countdown.value <= 0) {
      clearInterval(timer.value);
      isDisabled.value = false;
      countdown.value = 0;
      sessionStorage.setItem('hasReadAgreement', 'true');
    }
  }, 1000);
};

/**
 * 打开签名模态框（跳转到签名页）
 */
const openSignatureModal = () => {
  if (isDisabled.value) return; // 倒计时未结束，禁止签名
  router.push('/qianming');
};

/**
 * 确认提交签名
 */
const handleConfirm = async () => {
  // 未签名提示
  if (!signatureImage.value) {
    ElMessage.warning('请先完成签名');
    return;
  }

  isSubmitting.value = true;

  try {
    // 1. 从 localStorage 获取 userInfo
    const userInfoStr = localStorage.getItem('userInfo');
    if (!userInfoStr) {
      throw new Error('用户信息缺失，请重新登录！');
    }

    let userInfo;
    try {
      userInfo = JSON.parse(userInfoStr);
    } catch (e) {
      localStorage.removeItem('userInfo');
      throw new Error('用户信息格式错误，请重新登录！');
    }

    // 2. 检查身份证号是否存在
    const queChildrenSfz = userInfo.queChildrenSfz;
    if (!queChildrenSfz) {
      throw new Error('缺少身份证号信息，无法提交，请重新登录！');
    }

    // 3. 准备提交的数据（base64 转 Blob）
    const blob = base64ToBlob(signatureImage.value);
    const formData = new FormData();
    formData.append('file', blob, 'signature.png'); // 签名图片
    formData.append('psId', queChildrenSfz);       // 身份证号

    // 4. 调用上传接口
    const response = await request({
      url: '/api/questionnaires/uploadParentSign',
      method: 'post',
      data: formData,
      // 移除手动设置的 Content-Type，axios 会自动处理 FormData
    });

    // 检查响应是否有效
    if (!response || response.code !== 200) {
      throw new Error(response?.message || '签名提交失败：服务器内部错误');
    }

    // 提交成功处理
    ElMessage.success('签名提交成功！');
    sessionStorage.removeItem('signatureImage'); // 清除签名缓存
    router.push('/survey'); // 跳转到问卷页面

  } catch (error) {
    console.error('签名提交失败：', error);
    ElMessage.error(error.message || '网络异常，签名提交失败！');
  } finally {
    isSubmitting.value = false;
  }
};

/**
 * base64 字符串转 Blob 对象
 * @param {string} base64String - 图片的 base64 编码
 * @returns {Blob} - 转换后的 Blob 对象
 */
const base64ToBlob = (base64String) => {
  const parts = base64String.split(';base64,');
  if (parts.length < 2) {
    throw new Error('无效的 base64 字符串');
  }
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
};
</script>

<style scoped>
/* 页面整体样式 */
.agreement-page {
  padding: 16px;
  font-size: 14px;
  background-color: #f5f5f5;
  min-height: 100vh;
}
.content-wrapper {
  max-width: 800px;
  margin: 0 auto;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

/* 协议内容样式 */
.agreement-content {
  line-height: 1.8;
  padding: 10px 0;
  font-size: 15px;
  color: #333;
}
.agreement-content h2 {
  text-align: center;
  margin-bottom: 20px;
  color: #000;
}
.agreement-content p.no-indent{
    color: #000;
    text-indent: 0px;
    font-weight: 500;
    text-align: left;
}
.agreement-content p.p1 {
    color: #000;
    text-indent: 2em;
    margin-bottom: 12px;
    text-align: left;
}
.agreement-content p.p2 {
    color: #000;
    margin-bottom: 12px;
    text-align: left;
}
.agreement-content p.p3 {
    color: #000;
    text-indent: 2em;
    margin-bottom: 12px;
    text-align: left;
}
.agreement-content ul {
    color: #000;
    margin-bottom: 12px;
    text-align: left;
}
.agreement-content ul.li1 {
    color: #000;
    text-align: left;
}

/* 签名区域样式 */
.signature-area {
  margin: 30px 0;
}
.signature-preview {
  width: 100%;
  height: 150px;
  border: 1px dashed #ccc;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9f9f9;
  cursor: pointer;
  margin-bottom: 15px;
}
.signature-img {
  width: 100%;
  height: 150px;
  border: 1px solid #eee;
  border-radius: 4px;
  object-fit: contain;
  margin-bottom: 15px;
}
.sign-hint {
  color: #999;
  font-size: 14px;
}
.sign-btn {
  width: 100%;
}

/* 确认按钮样式 */
.next-btn {
  width: 100%;
  height: 50px;
  font-size: 16px;
}
:deep(.custom-btn) {
  background-color: #1989fa !important;
  color: #fff !important;
  border: none !important;
}
:deep(.custom-btn:disabled) {
  background-color: #ccc !important;
}
</style>